// @spec GHG-STR-022
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGHGOrganizationId } from '@/features/ghg/api/use-ghg-organization-id';
import {
  useCalculateWasteEmissions,
  useWasteActivityData,
} from '@/features/ghg/api/use-waste-calculations';
import { WasteCalculationResults } from '@/features/ghg/components/WasteCalculationResults';
import { WasteDataEntryForm } from '@/features/ghg/components/WasteDataEntryForm';
import { WasteStreamChart } from '@/features/ghg/components/WasteStreamChart';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/waste')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.wasteDisposal'),
  }),
  component: WastePage,
});

/**
 * Scope 3 Category 5 (Waste Generated in Operations) emissions page.
 *
 * Provides:
 * - Waste activity data entry by type and disposal method
 * - Emission calculations using DEFRA/EPA factors
 * - Results breakdown by waste type and disposal method
 * - Diversion rate tracking
 * - Visualization via stacked bar chart
 */
function WastePage() {
  const { t } = useTranslation('ghg');
  const { isLoaded: isOrgLoaded } = useOrganization();
  const orgId = useGHGOrganizationId();
  const { periodId } = useGHGPagePeriod();
  const isPeriodLocked = false as boolean; // placeholder for future period locking
  const [showEntryForm, setShowEntryForm] = useState(false);

  // Fetch waste activity data with proper filter object
  const {
    activityData: wasteData,
    isLoading: dataLoading,
    refetch,
  } = useWasteActivityData({ reportingPeriodId: periodId });

  // Calculate emissions mutation
  const calculateMutation = useCalculateWasteEmissions();

  const handleCalculate = () => {
    if (periodId && !isPeriodLocked) {
      calculateMutation.mutate({ reportingPeriodId: periodId });
    }
  };

  const handleEntrySuccess = () => {
    setShowEntryForm(false);
    void refetch();
  };

  if (!isOrgLoaded || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-5 w-80" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('wastePage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('wastePage.description')}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Button
              disabled={isPeriodLocked}
              onClick={() => {
                setShowEntryForm(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('wastePage.addWasteData')}
            </Button>
          </div>
        </header>

        <Tabs defaultValue="overview" className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">{t('wastePage.tabOverview')}</TabsTrigger>
              <TabsTrigger value="data">{t('wastePage.tabActivityData')}</TabsTrigger>
              <TabsTrigger value="results">{t('wastePage.tabCalculationResults')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('wastePage.totalEntries')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-5 w-5 text-muted-foreground" />
                      <span className="text-2xl font-bold">{wasteData.length}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('wastePage.wasteActivityRecords')}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('wastePage.wasteTypesTracked')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {new Set(wasteData.map((d) => d.wasteType)).size}
                    </div>
                    <p className="text-xs text-muted-foreground">{t('wastePage.differentWasteCategories')}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('wastePage.disposalMethods')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {new Set(wasteData.map((d) => d.disposalMethod)).size}
                    </div>
                    <p className="text-xs text-muted-foreground">{t('wastePage.methodsInUse')}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Chart - show data from calculation results if available */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('wastePage.wasteByStream')}</CardTitle>
                  <CardDescription>
                    {t('wastePage.wasteByStreamDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {calculateMutation.data?.byWasteType ? (
                    <WasteStreamChart data={calculateMutation.data.byWasteType} />
                  ) : (
                    <div className="flex h-64 items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <p>{t('wastePage.noCalculationResults')}</p>
                        <p className="text-sm">
                          {t('wastePage.runCalculateEmissions')}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="data">
              <Card>
                <CardHeader>
                  <CardTitle>{t('wastePage.tabActivityData')}</CardTitle>
                  <CardDescription>
                    {t('wastePage.activityDataDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dataLoading ? (
                    <div className="flex justify-center py-8">
                      <Skeleton className="h-32 w-full" />
                    </div>
                  ) : wasteData.length > 0 ? (
                    <div className="space-y-4">
                      {wasteData.map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between rounded-lg border p-4"
                        >
                          <div>
                            <p className="font-medium">{entry.wasteTypeName}</p>
                            <p className="text-sm text-muted-foreground">
                              {entry.quantity} {entry.unit} - {entry.disposalMethodName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{entry.dataQuality}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Trash2 className="h-12 w-12 text-muted-foreground opacity-50" />
                      <p className="mt-4 text-muted-foreground">{t('wastePage.noWasteData')}</p>
                      <Button
                        className="mt-4"
                        disabled={isPeriodLocked}
                        onClick={() => {
                          setShowEntryForm(true);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('wastePage.addFirstEntry')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="results">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t('wastePage.emissionCalculations')}</CardTitle>
                    <CardDescription>
                      {t('wastePage.emissionCalculationsDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleCalculate}
                    disabled={calculateMutation.isPending || isPeriodLocked}
                  >
                    {t('wastePage.calculateEmissions')}
                  </Button>
                </CardHeader>
                <CardContent>
                  <WasteCalculationResults
                    calculation={calculateMutation.data}
                    isLoading={calculateMutation.isPending}
                    onRecalculate={handleCalculate}
                  />
                </CardContent>
              </Card>
            </TabsContent>
        </Tabs>
      </div>

      {/* Add Entry Dialog */}
      <Dialog open={showEntryForm} onOpenChange={setShowEntryForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('wastePage.addWasteActivityData')}</DialogTitle>
          </DialogHeader>
          <WasteDataEntryForm
            mode="create"
            defaultReportingPeriodId={periodId}
            onSuccess={handleEntrySuccess}
            onCancel={() => {
              setShowEntryForm(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
